import { CpmChip, CpmIconButton } from '../../../components/design-system';
import { CONFIG_TEMPLATE_COPY } from '../configTemplateCopy';
import SettingsInput from './SettingsInput';

export interface WeekdayTimingRow {
    day: number;
    openingTime: string;
    closingTime: string;
}

const DAYS = [
    { day: 1, label: 'Mon', full: 'Monday' },
    { day: 2, label: 'Tue', full: 'Tuesday' },
    { day: 3, label: 'Wed', full: 'Wednesday' },
    { day: 4, label: 'Thu', full: 'Thursday' },
    { day: 5, label: 'Fri', full: 'Friday' },
    { day: 6, label: 'Sat', full: 'Saturday' },
    { day: 7, label: 'Sun', full: 'Sunday' },
] as const;

function clockFromIso(value: string): string {
    const match = /T(\d{2}):(\d{2})/.exec(value);
    if (!match) return typeof value === 'string' && /^\d{2}:\d{2}$/.test(value) ? value : '';
    return `${match[1]}:${match[2]}`;
}

function isoFromClock(clock: string): string {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(clock);
    if (!match) return clock;
    return `1970-01-01T${match[1]}:${match[2]}:00.000Z`;
}

function CloseIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
        </svg>
    );
}

interface WeekdayTimingsEditorProps {
    value: WeekdayTimingRow[];
    includedDays: number[];
    onToggleDay: (day: number, included: boolean) => void;
    onChangeRow: (day: number, patch: Partial<WeekdayTimingRow>) => void;
    disabled?: boolean;
}

export default function WeekdayTimingsEditor({
    value,
    includedDays,
    onToggleDay,
    onChangeRow,
    disabled = false,
}: WeekdayTimingsEditorProps) {
    const byDay = new Map(value.map((row) => [row.day, row]));
    const included = DAYS.filter(({ day }) => includedDays.includes(day));

    return (
        <div>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
                {DAYS.map(({ day, label }) => (
                    <CpmChip
                        key={day}
                        variant="filter"
                        active={includedDays.includes(day)}
                        disabled={disabled}
                        onClick={() => onToggleDay(day, !includedDays.includes(day))}
                    >
                        {label}
                    </CpmChip>
                ))}
            </div>
            <div className="flex flex-col" style={{ gap: 6, marginTop: 9 }}>
                {included.map(({ day, full }) => {
                    const row = byDay.get(day);
                    return (
                        <div key={day} className="flex items-center" style={{ gap: 8 }}>
                            <span
                                className="flex-none"
                                style={{
                                    width: 78,
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    color: 'var(--cpm-text-primary)',
                                }}
                            >
                                {full}
                            </span>
                            <SettingsInput
                                type="time"
                                disabled={disabled}
                                value={row ? clockFromIso(row.openingTime) : ''}
                                placeholder="10:00"
                                onChange={(next) =>
                                    onChangeRow(day, {
                                        openingTime: isoFromClock(next),
                                    })
                                }
                                width={96}
                            />
                            <span
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                to
                            </span>
                            <SettingsInput
                                type="time"
                                disabled={disabled}
                                value={row ? clockFromIso(row.closingTime) : ''}
                                placeholder="22:00"
                                onChange={(next) =>
                                    onChangeRow(day, {
                                        closingTime: isoFromClock(next),
                                    })
                                }
                                width={96}
                            />
                            <CpmIconButton
                                icon={<CloseIcon />}
                                label={CONFIG_TEMPLATE_COPY.TAKE_DAY_OUT}
                                size="sm"
                                tone="danger"
                                disabled={disabled}
                                onClick={() => onToggleDay(day, false)}
                            />
                        </div>
                    );
                })}
            </div>
            {included.length === 0 ? (
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'var(--cpm-text-secondary)',
                        marginTop: 8,
                    }}
                >
                    {CONFIG_TEMPLATE_COPY.EMPTY_WEEKDAY}
                </div>
            ) : null}
        </div>
    );
}
